import "@/i18n";
import { lazy, Suspense, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  queryClient,
  queryPersister,
  QUERY_PERSIST_BUSTER,
  QUERY_PERSIST_MAX_AGE,
} from "@/lib/query-client";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { RadioProvider } from "@/contexts/RadioContext";
import { PresenceProvider } from "@/contexts/PresenceContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/ScrollToTop";
import useCheckoutDeepLink from "@/hooks/useCheckoutDeepLink";
import useDialDeepLink from "@/hooks/useDialDeepLink";
import useCarRecentsSync from "@/hooks/useCarRecentsSync";
import useCarContactsSync from "@/hooks/useCarContactsSync";
import useCarDialBridge from "@/hooks/useCarDialBridge";
import useAndroidBackNavigation from "@/hooks/useAndroidBackNavigation";
// ElefaLoadingScreen still rendered by NativeRootGate at cold launch; route-level Suspense now uses PageSkeleton.

// Tiny invisible component so we can call the router-aware hook
// inside <BrowserRouter>. Bridges Stripe Checkout's elefa:// deep
// link back into the in-app success/cancel screens.
function CheckoutDeepLinkBridge() {
  useCheckoutDeepLink();
  return null;
}

// Handles elefa://dial?to=<e164> deep links from CarPlay /
// Android Auto / Siri / shortcuts → routes into the dialer with
// the number pre-filled and auto-call armed.
function DialDeepLinkBridge() {
  useDialDeepLink();
  return null;
}

// Mirrors the user's recent elefa call history into Capacitor
// Preferences so the native CarPlay / Android Auto surfaces can
// render a "Recents" list without re-implementing data fetching.
function CarRecentsSyncBridge() {
  useCarRecentsSync();
  return null;
}

// R1 — Mirrors the user's accepted-connection peers (name + E.164) into
// Capacitor Preferences so the native CarPlay / Android Auto Contacts
// tab can render an A–Z list with one-tap dial. Same guardrails as
// CarRecentsSyncBridge: no infra touched, no new RPC.
function CarContactsSyncBridge() {
  useCarContactsSync();
  return null;
}

// R5 — Listens for `carDialRequested` from native ElefaCallKit (iOS
// CarPlay scene posts ElefaCarCallerDial notification). Routes into the
// dialer so call placement converges on the existing JS path. iOS-only
// in practice; inert until Apple grants the CarPlay entitlement.
function CarDialBridge() {
  useCarDialBridge();
  return null;
}

// Wires Android's hardware/predictive back-button into React Router so
// the OS back gesture pops history (and triggers the slide-back
// transition) instead of exiting the app. iOS unaffected.
function NativeBackBridge() {
  useAndroidBackNavigation();
  return null;
}
import { NativeAppProvider } from "@/components/NativeAppProvider";


import { AnalyticsProvider } from "@/components/marketing/AnalyticsProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import { ElefaCallProvider } from "@/contexts/ElefaCallContext";
import { NativeSipCallProvider } from "@/contexts/NativeSipCallContext";
import { ActiveCallReturnBar } from "@/components/connect/ActiveCallReturnBar";
import { PerfHud } from "@/components/dev/PerfHud";
const ConnectDashboard = lazy(() => import("./pages/connect-portal/ConnectDashboard"));
const ConnectDialer = lazy(() => import("./pages/connect-portal/ConnectDialer"));
import { BrowserDialerGate } from "@/components/connect/BrowserDialerGate";
const ConnectAuthPage = lazy(() => import("./pages/connect-portal/ConnectAuthPage"));

// ─── Lazy page imports ───

// Public pages
// Native-aware root gate: web → ConnectHome marketing, native → auth/dashboard
import { NativeRootGate } from "./components/native/NativeRootGate";
const Support = lazy(() => import("./pages/Support"));
const Auth = lazy(() => import("./pages/Auth"));
const About = lazy(() => import("./pages/About"));
const Referral = lazy(() => import("./pages/Referral"));
const PublicPricingPage = lazy(() => import("./pages/public/PublicPricingPage"));
const PublicHowItWorksPage = lazy(() => import("./pages/public/PublicHowItWorksPage"));
const PublicDownloadPage = lazy(() => import("./pages/public/PublicDownloadPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Legal
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const FinancialHardshipPolicy = lazy(() => import("./pages/FinancialHardshipPolicy"));
const RegulatoryInformation = lazy(() => import("./pages/RegulatoryInformation"));
const LegalInformation = lazy(() => import("./pages/LegalInformation"));
const Refunds = lazy(() => import("./pages/legal/Refunds"));
const DeleteAccountRequest = lazy(() => import("./pages/legal/DeleteAccountRequest"));

// Admin
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminHub = lazy(() => import("./pages/admin/AdminHub"));
const SiteSettings = lazy(() => import("./pages/admin/SiteSettings"));
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const AdminRatesPage = lazy(() => import("./pages/admin/AdminRatesPage"));
const CRMDashboard = lazy(() => import("./pages/admin/CRMDashboard"));
const CustomerList = lazy(() => import("./pages/admin/CustomerList"));
const CustomerDetail = lazy(() => import("./pages/admin/CustomerDetail"));
const SecurityCenter = lazy(() => import("./pages/admin/SecurityCenter"));
const CreditRecovery = lazy(() => import("./pages/admin/CreditRecovery"));
const LowBalanceMonitor = lazy(() => import("./pages/admin/LowBalanceMonitor"));
const BillingDashboard = lazy(() => import("./pages/admin/BillingDashboard"));
const RadioAnalytics = lazy(() => import("./pages/admin/RadioAnalytics"));
const AdminBroadcasts = lazy(() => import("./pages/admin/AdminBroadcasts"));
const Treasury = lazy(() => import("./pages/admin/Treasury"));


// SEO landing pages
const CallHome = lazy(() => import("./pages/connect-portal/landing/CallHome"));
const SaveOnCalls = lazy(() => import("./pages/connect-portal/landing/SaveOnCalls"));
const NoExpiry = lazy(() => import("./pages/connect-portal/landing/NoExpiry"));
const HDCalling = lazy(() => import("./pages/connect-portal/landing/HDCalling"));

// Connect Portal
const ConnectHome = lazy(() => import("./pages/connect-portal/ConnectHome"));
const ConnectRatesPage = lazy(() => import("./pages/connect-portal/ConnectRatesPage"));
const ConnectTopUpPage = lazy(() => import("./pages/connect-portal/ConnectTopUpPage"));
const ConnectTopUpSuccessPage = lazy(() => import("./pages/connect-portal/ConnectTopUpSuccessPage"));
const ConnectHistoryPage = lazy(() => import("./pages/connect-portal/ConnectHistoryPage"));
const ConnectMessagesPage = lazy(() => import("./pages/connect-portal/ConnectMessagesPage"));
const ConnectAddPage = lazy(() => import("./pages/connect-portal/ConnectAddPage"));
const ConnectReferralsPage = lazy(() => import("./pages/connect-portal/ConnectReferralsPage"));
// ConnectSettingsPage merged into ConnectMePage
const ConnectHelpPage = lazy(() => import("./pages/connect-portal/ConnectHelpPage"));
const ConnectAboutPage = lazy(() => import("./pages/connect-portal/ConnectAboutPage"));
const ConnectHowItWorksPage = lazy(() => import("./pages/connect-portal/ConnectHowItWorksPage"));
const ConnectBillingInfoPage = lazy(() => import("./pages/connect-portal/ConnectBillingInfoPage"));
const ConnectRadioPage = lazy(() => import("./pages/connect-portal/ConnectRadioPage"));
const ConnectRadioLanding = lazy(() => import("./pages/connect-portal/ConnectRadioLanding"));
const ConnectMePage = lazy(() => import("./pages/connect-portal/ConnectMePage"));
const ConnectLegalHubPage = lazy(() => import("./pages/connect-portal/ConnectLegalHubPage"));
const ConnectScanPage = lazy(() => import("./pages/connect-portal/ConnectScanPage"));
const ConnectMyQRPage = lazy(() => import("./pages/connect-portal/ConnectMyQRPage"));
const ConnectContactsPage = lazy(() => import("./pages/connect-portal/ConnectContactsPage"));
const ConnectNotificationsPage = lazy(() => import("./pages/connect-portal/ConnectNotificationsPage"));
const ConnectNotificationPreferencesPage = lazy(() => import("./pages/connect-portal/ConnectNotificationPreferencesPage"));
// ConnectPulsePage removed from navigation

// Dynamic country route (handles ALL country + diaspora pages)
const DynamicCountryRoute = lazy(() => import("./pages/connect-portal/countries/DynamicCountryRoute"));
const CorridorPage = lazy(() => import("./pages/corridor/CorridorPage"));

// Country-code SEO landing pages (long-tail diaspora keywords)
const CountryCodeHub = lazy(() => import("./pages/CountryCodeHub"));
const CountryCodePage = lazy(() => import("./pages/CountryCodePage"));

// Dev/test harnesses
const CallKitTest = lazy(() => import("./pages/dev/CallKitTest"));
const PaymentComplete = lazy(() => import("./pages/PaymentComplete"));

// Query client + disk persistence live in src/lib/query-client.ts (imported above).

function DeferredStartupProviders({ children }: { children: ReactNode }) {
  return (
    <PresenceProvider>
      <RadioProvider>
        <ElefaCallProvider>
          <NativeSipCallProvider>
            <ActiveCallReturnBar />
            {children}
          </NativeSipCallProvider>
        </ElefaCallProvider>
      </RadioProvider>
    </PresenceProvider>
  );
}

function PageFallback() {
  // Chrome-only skeleton for in-app route lazy-loads - keeps the bottom
  // nav and header visible while the chunk parses, so navigation feels
  // instant instead of flashing a full-screen branded loader on every
  // tab switch. The branded loader still runs at cold-launch via
  // NativeRootGate, so first-paint identity is preserved.
  return <PageSkeleton />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/" element={<NativeRootGate />} />

      {/* Public */}
      <Route path="/support" element={<Support />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/about" element={<About />} />
      <Route path="/referral" element={<Referral />} />
      <Route path="/payment-complete" element={<PaymentComplete />} />

      {/* Public marketing aliases — spec §5 */}
      <Route path="/pricing" element={<PublicPricingPage />} />
      <Route path="/how-it-works" element={<PublicHowItWorksPage />} />
      <Route path="/download" element={<PublicDownloadPage />} />
      <Route path="/rates" element={<Navigate to="/connect-portal/rates" replace />} />

      {/* Country-code SEO landing pages — see src/pages/CountryCodePage.tsx */}
      <Route path="/country-code" element={<CountryCodeHub />} />
      <Route path="/country-code/:slug" element={<CountryCodePage />} />

      {/* Legal */}
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/legal/refunds" element={<Refunds />} />
      {/* v3.3: fair use, cookies, availability and complaints now live inside
          Terms and Privacy; these paths redirect to the matching anchor. */}
      <Route path="/legal/fair-use" element={<Navigate to="/legal/terms#fair-use" replace />} />
      <Route path="/legal/complaints" element={<Navigate to="/legal/terms#complaints" replace />} />
      <Route path="/legal/financial-hardship" element={<FinancialHardshipPolicy />} />
      <Route path="/legal/cookies" element={<Navigate to="/legal/privacy#collect" replace />} />
      <Route path="/legal/availability" element={<Navigate to="/legal/terms#availability" replace />} />
      <Route path="/legal/service-availability" element={<Navigate to="/legal/terms#availability" replace />} />
      <Route path="/legal/delete-account-request-9f2a1c" element={<DeleteAccountRequest />} />
      <Route path="/legal/delete-account" element={<Navigate to="/legal/delete-account-request-9f2a1c" replace />} />
      <Route path="/regulatory" element={<RegulatoryInformation />} />
      <Route path="/legal" element={<LegalInformation />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/fair-use" element={<Navigate to="/legal/fair-use" replace />} />
      <Route path="/complaints" element={<Navigate to="/legal/complaints" replace />} />
      <Route path="/hardship" element={<Navigate to="/legal/financial-hardship" replace />} />

      {/* Admin */}
      <Route path="/admin" element={<Navigate to="/admin/hub" replace />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/hub" element={<AdminHub />} />
      <Route path="/admin/settings" element={<SiteSettings />} />
      <Route path="/admin/users" element={<UserManagement />} />
      <Route path="/admin/rates" element={<AdminRatesPage />} />
      <Route path="/admin/billing" element={<BillingDashboard />} />
      <Route path="/admin/treasury" element={<Treasury />} />
      <Route path="/admin/credit-recovery" element={<CreditRecovery />} />
      <Route path="/admin/low-balance" element={<LowBalanceMonitor />} />
      <Route path="/admin/radio-analytics" element={<RadioAnalytics />} />
      <Route path="/admin/broadcasts" element={<AdminBroadcasts />} />
      <Route path="/admin/security" element={<SecurityCenter />} />
      <Route path="/admin/crm" element={<CRMDashboard />} />
      <Route path="/admin/crm/customers" element={<CustomerList />} />
      <Route path="/admin/crm/customers/:customerId" element={<CustomerDetail />} />

      {/* SEO Landing Pages */}
      <Route path="/call-home" element={<CallHome />} />
      <Route path="/save-on-calls" element={<SaveOnCalls />} />
      <Route path="/no-expiry" element={<NoExpiry />} />
      <Route path="/hd-calling" element={<HDCalling />} />
      {/* Legacy redirects for old landing pages */}
      <Route path="/call-africa" element={<Navigate to="/call-home" replace />} />
      <Route path="/call-south-sudan" element={<Navigate to="/connect-portal/south-sudan" replace />} />
      <Route path="/call-ethiopia" element={<Navigate to="/connect-portal/ethiopia" replace />} />
      <Route path="/call-nigeria" element={<Navigate to="/connect-portal/nigeria" replace />} />
      <Route path="/call-kenya" element={<Navigate to="/connect-portal/kenya" replace />} />
      <Route path="/call-uganda" element={<Navigate to="/connect-portal/uganda" replace />} />

      {/* Corridor pages (spec §6 template) — /call/{country} */}
      <Route path="/call/:corridor" element={<CorridorPage />} />



      {/* Connect Portal */}
      <Route path="/connect-portal" element={<Navigate to="/" replace />} />
      <Route path="/connect-portal/dashboard" element={<ConnectDashboard />} />
      <Route path="/connect-portal/dial" element={<BrowserDialerGate><ConnectDialer /></BrowserDialerGate>} />
      <Route path="/connect-portal/rates" element={<ConnectRatesPage />} />
      <Route path="/connect-portal/topup" element={<ConnectTopUpPage />} />
      <Route path="/connect-portal/topup/success" element={<ConnectTopUpSuccessPage />} />
      <Route path="/connect-portal/history" element={<ConnectHistoryPage />} />
      <Route path="/connect-portal/messages" element={<ConnectMessagesPage />} />
      <Route path="/connect-portal/add" element={<ConnectAddPage />} />
      <Route path="/connect-portal/referrals" element={<ConnectReferralsPage />} />
      <Route path="/connect-portal/settings" element={<Navigate to="/connect-portal/me" replace />} />
      <Route path="/connect-portal/help" element={<ConnectHelpPage />} />
      <Route path="/connect-portal/auth" element={<ConnectAuthPage />} />
      <Route path="/connect-portal/signup" element={<Navigate to="/connect-portal/auth" replace />} />
      <Route path="/connect-portal/login" element={<Navigate to="/connect-portal/auth" replace />} />
      {/* Legal docs are single-canonical at /legal/*; the in-app portal
          routes redirect so Google indexes one URL per document. */}
      <Route path="/connect-portal/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/connect-portal/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/connect-portal/about" element={<ConnectAboutPage />} />
      <Route path="/connect-portal/how-it-works" element={<ConnectHowItWorksPage />} />
      <Route path="/connect-portal/billing-info" element={<ConnectBillingInfoPage />} />
      <Route path="/connect-portal/radio" element={<ConnectRadioPage />} />
      <Route path="/connect-portal/free-radio" element={<ConnectRadioLanding />} />
      <Route path="/connect-portal/me" element={<ConnectMePage />} />
      <Route path="/connect-portal/legal" element={<ConnectLegalHubPage />} />
      <Route path="/connect-portal/scan" element={<ConnectScanPage />} />
      <Route path="/connect-portal/my-qr" element={<ConnectMyQRPage />} />
      <Route path="/connect-portal/contacts" element={<ConnectContactsPage />} />
      <Route path="/connect-portal/notifications" element={<ConnectNotificationsPage />} />
      <Route path="/connect-portal/notifications/preferences" element={<ConnectNotificationPreferencesPage />} />
      {/* Pulse removed - route preserved as redirect */}
      <Route path="/connect-portal/pulse" element={<Navigate to="/connect-portal/me" replace />} />
      <Route path="/connect-portal/invite" element={<Navigate to="/connect-portal/referrals" replace />} />

      {/* Dynamic country pages - handles all countries + diaspora origins */}
      <Route path="/connect-portal/:countrySlug" element={<DynamicCountryRoute />} />

      {/* Legacy redirects */}
      <Route path="/connect" element={<Navigate to="/connect-portal" replace />} />
      <Route path="/connect/*" element={<Navigate to="/connect-portal" replace />} />
      {/* Note: /auth is now the canonical unified entry; /signup and /login render the same UI */}
      <Route path="/connect-portal/dialer" element={<Navigate to="/connect-portal/dashboard" replace />} />
      <Route path="/connect-portal/profile" element={<Navigate to="/connect-portal/me" replace />} />
      <Route path="/account/security" element={<Navigate to="/connect-portal/me" replace />} />
      <Route path="/portal" element={<Navigate to="/connect-portal/dashboard" replace />} />
      {/* Legacy SIM-card-era routes - redirect to current VoIP equivalents so historical links don't 404 */}
      <Route path="/plans" element={<Navigate to="/connect-portal/rates" replace />} />
      <Route path="/activate" element={<Navigate to="/connect-portal/auth" replace />} />
      <Route path="/critical-information" element={<Navigate to="/regulatory" replace />} />
      <Route path="/terms-of-service" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/privacy-policy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/portal/*" element={<Navigate to="/connect-portal/dashboard" replace />} />
      <Route path="/connect-portal/admin/*" element={<Navigate to="/admin/hub" replace />} />

      {/* Dead-link rescue - every previously broken in-app link now resolves
          to the closest live destination instead of 404'ing. Keep these as
          aliases so external bookmarks, marketing emails, and old screenshots
          continue to work. (App-IA audit, Apple Wallet pass, 2026-05) */}
      <Route path="/add-ons" element={<Navigate to="/connect-portal/topup" replace />} />
      <Route path="/coverage" element={<Navigate to="/connect-portal/rates" replace />} />
      <Route path="/coming-soon" element={<Navigate to="/connect-portal/about" replace />} />
      <Route path="/full-site" element={<Navigate to="/" replace />} />
      <Route path="/admin/investors" element={<Navigate to="/admin/hub" replace />} />
      <Route path="/admin/waitlist" element={<Navigate to="/admin/users" replace />} />
      <Route path="/admin/crm/security" element={<Navigate to="/admin/security" replace />} />
      <Route path="/admin/crm/credits" element={<Navigate to="/admin/credit-recovery" replace />} />
      <Route path="/admin/crm/notifications" element={<Navigate to="/admin/broadcasts" replace />} />

      {/* Dev/test harnesses */}
      <Route path="/dev/callkit-test" element={<CallKitTest />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

const App = () => {
  return (
  <ErrorBoundary>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        maxAge: QUERY_PERSIST_MAX_AGE,
        buster: QUERY_PERSIST_BUSTER,
      }}
    >
      <NativeAppProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>

            <AnalyticsProvider>
              <DeferredStartupProviders>
                <ScrollToTop />
                <CheckoutDeepLinkBridge />
                <DialDeepLinkBridge />
                <CarRecentsSyncBridge />
                <CarContactsSyncBridge />
                <CarDialBridge />
                <NativeBackBridge />
                <PerfHud />
                
                <AppRoutes />
              </DeferredStartupProviders>
            </AnalyticsProvider>
          </BrowserRouter>
        </AuthProvider>
      </NativeAppProvider>
    </PersistQueryClientProvider>
  </ErrorBoundary>
  );
};

export default App;
